import { useMemo, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Pretty-prints JSON with minimal syntax highlighting — no external lib so
 * the bundle stays small. Falls back to raw text if the input isn't valid JSON.
 *
 * Accepts a JSON string OR a pre-parsed object; if you have an object,
 * stringify it before passing.
 */
export function JsonViewer({ json, className }: { json: string | null | undefined; className?: string }) {
  const formatted = useMemo(() => {
    if (json == null) return null;
    const trimmed = String(json).trim();
    if (!trimmed) return null;
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed; // not JSON — render verbatim
    }
  }, [json]);

  if (formatted == null) {
    return <div className="text-xs text-text-faint italic">empty</div>;
  }

  return (
    <pre
      className={cn(
        'text-xs font-mono whitespace-pre-wrap break-words',
        'bg-bg-2 border border-[color:var(--border)] rounded-card-tight p-3',
        'max-h-[60vh] overflow-auto scroll',
        className,
      )}
    >
      <code>{highlight(formatted)}</code>
    </pre>
  );
}

/**
 * Wraps tokens in tiny spans so Tailwind can color them. Cheap regex pass —
 * not a real tokenizer; good enough for human inspection of agent output.
 */
function highlight(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const regex =
    /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [match, k, str, lit, num, punct] = m;
    if (k != null)         out.push(<span key={key++} className="text-kam-cyan">{match}</span>);
    else if (str != null)  out.push(<span key={key++} className="text-kam-green">{match}</span>);
    else if (lit != null)  out.push(<span key={key++} className="text-kam-violet">{match}</span>);
    else if (num != null)  out.push(<span key={key++} className="text-kam-amber">{match}</span>);
    else if (punct != null)out.push(<span key={key++} className="text-text-faint">{match}</span>);
    last = m.index + match.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
